/**
 * RoleProtectedRoute - Protege rutas basado en roles de usuario
 * Redirige al dashboard si el usuario no tiene el rol necesario
 */
import { Navigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';
import { Box, Typography, Button } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * @component RoleProtectedRoute
 * @description Componente que verifica si el usuario tiene uno de los roles permitidos
 * @param {Array<string>} allowedRoles - Array de roles permitidos
 * @param {React.ReactNode} children - Componente hijo a renderizar si tiene permiso
 */
const RoleProtectedRoute = ({ allowedRoles, children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Si no hay usuario autenticado, redirigir al login (esto no debería pasar porque PrivateRoute lo maneja)
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Verificar si el usuario tiene uno de los roles permitidos
  const hasPermission = allowedRoles.includes(user.rol);

  // Si no tiene permiso, mostrar mensaje de acceso denegado
  if (!hasPermission) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          textAlign: 'center',
          p: 3,
        }}
      >
        <LockIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Acceso Denegado
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500 }}>
          No tienes permisos para acceder a esta página. Si crees que esto es un error,
          contacta al administrador del sistema.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Volver al Dashboard
        </Button>
      </Box>
    );
  }

  // Si tiene permiso, renderizar el componente hijo
  return children;
};

RoleProtectedRoute.propTypes = {
  allowedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  children: PropTypes.node.isRequired,
};

export default RoleProtectedRoute;
